import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../img/logo.webp';
import FavoritesShortcut from './FavoritesShortcut';
import '../../css/SiteLayout.css';

export { SITE_CONTACTS } from '../../config/siteContacts';

export const SITE_NAVIGATION = [
    { label: 'Richiedi un preventivo', href: '/richiedi-preventivo' },
    { label: 'Chi siamo', href: '/#chi-siamo' },
    { label: 'Servizi Funebri', href: '/#servizi-funebri' },
    { label: 'Guida al Lutto', href: '/#guida-al-lutto' },
    { label: 'Contatti', href: '/#contatti' },
];

export function scrollToHomeSection(event, href, location) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // Clicking an already active anchor does not trigger a router location change.
    if (`${location.pathname}${location.hash}` === href) {
        const target = document.getElementById(href.split('#')[1]);
        if (target) {
            event.preventDefault();
            target.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'start',
            });
        }
    }
}

export default function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [agencyOpen, setAgencyOpen] = useState(false);
    const menuButton = useRef(null);
    const agencyButton = useRef(null);
    const agencyMenu = useRef(null);
    const header = useRef(null);
    const location = useLocation();

    useEffect(() => {
        setMenuOpen(false);
        setAgencyOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        if (!menuOpen && !agencyOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (agencyOpen && agencyButton.current?.getClientRects().length) {
                    setAgencyOpen(false);
                    agencyButton.current?.focus();
                } else {
                    setAgencyOpen(false);
                    setMenuOpen(false);
                    menuButton.current?.focus();
                }
            }
        };
        const handleOutsideClick = (event) => {
            if (!header.current?.contains(event.target)) setMenuOpen(false);
            if (!agencyMenu.current?.contains(event.target)) setAgencyOpen(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('pointerdown', handleOutsideClick);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('pointerdown', handleOutsideClick);
        };
    }, [menuOpen, agencyOpen]);

    const handleSectionClick = (event, href) => {
        scrollToHomeSection(event, href, location);
        setMenuOpen(false);
        setAgencyOpen(false);
    };

    return (
        <header className="ef-header" ref={header}>
            <div className="ef-header__inner">
                <Link className="ef-header__brand" to="/" aria-label="Easy Funeral, pagina iniziale" onClick={() => { setMenuOpen(false); setAgencyOpen(false); }}>
                    <img className="ef-header__brand-mark" src={logo} alt="" width="36" height="36" />
                    <span>Easy Funeral</span>
                </Link>

                <div className={`ef-header__navigation${menuOpen ? ' ef-header__navigation--open' : ''}`} id="ef-site-navigation">
                    <nav className="ef-header__links" aria-label="Navigazione principale">
                        {SITE_NAVIGATION.map(({ label, href }) => (
                            <Link
                                key={href}
                                to={href}
                                aria-current={`${location.pathname}${location.hash}` === href ? 'location' : undefined}
                                onClick={(event) => handleSectionClick(event, href)}
                            >
                                {label}
                            </Link>
                        ))}
                    </nav>
                    <div
                        className={`ef-header__agency${agencyOpen ? ' ef-header__agency--open' : ''}`}
                        ref={agencyMenu}
                        onBlur={event => {
                            if (!event.currentTarget.contains(event.relatedTarget)) setAgencyOpen(false);
                        }}
                    >
                        <button
                            ref={agencyButton}
                            type="button"
                            className="ef-header__agency-toggle"
                            aria-expanded={agencyOpen}
                            aria-controls="ef-agency-links"
                            onClick={() => setAgencyOpen(open => !open)}
                        >
                            Area agenzie
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
                        </button>
                        <nav id="ef-agency-links" className="ef-header__agency-panel" aria-label="Area agenzie">
                            <p>Per le onoranze funebri</p>
                            <Link to="/login" aria-current={location.pathname === '/login' ? 'page' : undefined} onClick={() => { setMenuOpen(false); setAgencyOpen(false); }}>Accedi <span aria-hidden="true">→</span></Link>
                            <Link to="/registrazione" aria-current={location.pathname === '/registrazione' ? 'page' : undefined} onClick={() => { setMenuOpen(false); setAgencyOpen(false); }}>Registra la tua agenzia <span aria-hidden="true">→</span></Link>
                        </nav>
                    </div>
                </div>
                <div className="ef-header__actions">
                    <FavoritesShortcut />
                    <button
                        ref={menuButton}
                        className="ef-header__menu-toggle"
                        type="button"
                        aria-label={menuOpen ? 'Chiudi il menu' : 'Apri il menu'}
                        aria-expanded={menuOpen}
                        aria-controls="ef-site-navigation"
                        onClick={() => { setMenuOpen(open => !open); setAgencyOpen(false); }}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                            {menuOpen ? <path d="m6 6 12 12M6 18 18 6" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
                        </svg>
                    </button>
                </div>
            </div>
        </header>
    );
}
